/**
 * TenantBillingExtras — bundled tenant surface for:
 *   • Digital receipts (list + open receipt HTML/PDF)
 *   • Autopay toggle
 *   • Credit reporting opt-in (Equifax Canada)
 *
 * All three call /api/tenant-extras/*.
 */
import { useEffect, useState, useCallback } from "react";
import { PageHeader, Card, Button, Pill, Loader, Input } from "@/components/ui/Primitives";
import { Receipt, Zap, CreditCard, ShieldCheck, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

export default function TenantBillingExtras() {
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [autopay, setAutopay] = useState(null);
  const [credit, setCredit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/api/tenant-extras/receipts`, { headers: auth() }).then((r) => r.json()),
        fetch(`${API}/api/tenant-extras/autopay`, { headers: auth() }).then((r) => r.json()),
        fetch(`${API}/api/tenant-extras/credit-opt-in`, { headers: auth() }).then((r) => r.json()),
      ]);
      setReceipts(r1.items || []);
      setAutopay(r2);
      setCredit(r3);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleAutopay() {
    try {
      const r = await fetch(`${API}/api/tenant-extras/autopay`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !autopay?.enabled, day_of_month: autopay?.day_of_month || 1 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Update failed");
      setAutopay(d);
      toast.success(d.enabled ? "Autopay enabled" : "Autopay turned off");
    } catch (err) { toast.error(String(err.message)); }
  }

  async function toggleCredit(signedName) {
    try {
      const r = await fetch(`${API}/api/tenant-extras/credit-opt-in`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !credit?.enabled, signed_name: signedName }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Update failed");
      setCredit(d);
      toast.success(d.enabled ? "Credit reporting activated" : "Credit reporting turned off");
    } catch (err) { toast.error(String(err.message)); }
  }

  if (loading) return <Loader/>;
  return (
    <div data-testid="tenant-billing-extras-page">
      <PageHeader title="Rent · Receipts · Autopay" subtitle="Everything about your rent payments in one place."/>

      {/* AUTOPAY */}
      <Card className="p-5 mb-4" data-testid="autopay-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-emerald-100 p-3"><Zap className="w-5 h-5 text-emerald-700"/></div>
            <div>
              <div className="font-display font-bold text-slate-900 flex items-center gap-2">Autopay {autopay?.enabled && <Pill tone="green">On</Pill>}</div>
              <p className="text-sm text-slate-600 mt-0.5">Never miss rent. We&apos;ll charge your saved card automatically when the next month&apos;s rent becomes due.</p>
            </div>
          </div>
          <Button onClick={toggleAutopay} variant={autopay?.enabled ? "secondary" : "primary"} className="!py-1.5 !px-3 text-xs" data-testid="autopay-toggle">
            {autopay?.enabled ? "Turn off" : "Enable"}
          </Button>
        </div>
      </Card>

      {/* CREDIT OPT-IN */}
      <CreditOptInCard credit={credit} onToggle={toggleCredit}/>

      {/* RECEIPTS */}
      <Card className="p-5" data-testid="receipts-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-xl bg-slate-100 p-3"><Receipt className="w-5 h-5 text-slate-700"/></div>
          <div>
            <div className="font-display font-bold text-slate-900">Digital receipts</div>
            <p className="text-sm text-slate-600 mt-0.5">A receipt is generated automatically for every completed rent payment.</p>
          </div>
        </div>
        {receipts.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No receipts yet — they&apos;ll appear here once your first payment clears.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {receipts.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3" data-testid={`receipt-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{r.period}</div>
                  <div className="text-xs text-slate-500">Paid {r.paid_at ? new Date(r.paid_at).toLocaleString() : ""}</div>
                </div>
                <div className="font-display font-extrabold text-slate-900 tabular-nums shrink-0">${((r.amount_cents || 0) / 100).toFixed(2)}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <a href={`${API}${r.receipt_url}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`receipt-view-${r.id}`}><ExternalLink className="w-3 h-3"/>View</a>
                  <a href={`${API}${r.pdf_url}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid={`receipt-pdf-${r.id}`}><Download className="w-3 h-3"/>PDF</a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function CreditOptInCard({ credit, onToggle }) {
  const [name, setName] = useState("");
  return (
    <Card className="p-5 mb-4" data-testid="credit-opt-in-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-blue-100 p-3"><ShieldCheck className="w-5 h-5 text-blue-700"/></div>
          <div>
            <div className="font-display font-bold text-slate-900 flex items-center gap-2">
              Credit reporting {credit?.enabled && <Pill tone="green">Active</Pill>}
            </div>
            <p className="text-sm text-slate-600 mt-0.5">Report your on-time rent payments to Equifax Canada to build your credit score.</p>
            {credit?.enabled ? (
              <div className="text-xs text-slate-500 mt-1">Signed by <b>{credit.signed_name}</b> on {credit.signed_at ? new Date(credit.signed_at).toLocaleDateString() : ""}</div>
            ) : (
              <div className="mt-2">
                <Input label="Type your full legal name to consent" value={name} onChange={(e) => setName(e.target.value)} testid="credit-signed-name" placeholder="Jane Q. Smith"/>
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={() => onToggle(name)}
          disabled={!credit?.enabled && !name.trim()}
          variant={credit?.enabled ? "secondary" : "primary"}
          className="!py-1.5 !px-3 text-xs shrink-0"
          data-testid="credit-opt-in-toggle"
        >
          {credit?.enabled ? "Turn off" : "Opt in"}
        </Button>
      </div>
    </Card>
  );
}
